import { Close, Settings, RecordVoiceOver } from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    Slider,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCalculator } from '../../../../contexts/CalculatorContext';
import VoiceBotSettingsDialog from './VoiceBotSettingsDialog';

const VoiceBot = () => {
  const { t } = useTranslation();
  const {
    voicebotMinutes = 1100,
    voicebotAiMode = true,
    voicebotSettingsOpen,
    hasVoicebotCustomSettings,
    setVoicebotMinutes,
    setVoicebotAiMode,
    setVoicebotSettingsOpen,
    setHasVoicebotCustomSettings,
    toggleProduct,
    priceCalculation,
    selectedProducts,
    veryCustomProductDiscounts
  } = useCalculator();


  const [inputValue, setInputValue] = useState(voicebotMinutes.toString());
  const [localSliderValue, setLocalSliderValue] = useState(voicebotMinutes);

  // Sync inputValue with voicebotMinutes when it changes externally (e.g., from slider)
  useEffect(() => {
    setInputValue(voicebotMinutes.toString());
    setLocalSliderValue(voicebotMinutes);
  }, [voicebotMinutes]);

  const handleMinutesChange = (event, newValue) => {
    setLocalSliderValue(newValue);
    setVoicebotMinutes(newValue);
    setInputValue(newValue.toString());
  };

  const handleInputChange = (event) => {
    // Allow any input while typing
    setInputValue(event.target.value);
  };

  const handleInputBlur = () => {
    // Validate and correct value only when losing focus
    const numValue = Number(inputValue) || 1100;
    const correctedValue = Math.max(1100, numValue); // Minimum 1100 minutes
    setVoicebotMinutes(correctedValue);
    setInputValue(correctedValue.toString());
  };

  const handleSettingsClick = () => {
    setVoicebotSettingsOpen(true);
  };

  // Check if there are any custom settings (including very custom discounts)
  const hasAnyCustomSettings = hasVoicebotCustomSettings || (veryCustomProductDiscounts['voicebot'] && veryCustomProductDiscounts['voicebot'] > 0);

  const getPlanDisplayName = (plan) => {
    const plans = {
      lite: 'Lite',
      pro: 'Pro',
      max: 'Max',
      scale: 'Scale'
    };
    return plans[plan] || plan;
  };

  const renderPricingInfo = () => {
    // Check if pricing calculation is available
    if (!priceCalculation) {
      return null;
    }

    // Try both summaryItemsWithDiscounts and summaryItems
    const summaryItems = priceCalculation.summaryItemsWithDiscounts || priceCalculation.summaryItems;

    if (!summaryItems) {
      return null;
    }

    // Get VoiceBot data from calculator context
    const voicebotProduct = summaryItems.find(item => item.productType === 'voicebot');

    if (!voicebotProduct) {
      return null;
    }

    const { selected_plan, included_units, extra_units, price_per_unit } = voicebotProduct;

    return (
      <Box sx={{
        mt: 2,
        p: 1.5,
        bgcolor: 'rgba(0, 200, 83, 0.05)',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('calculator.tariff', 'Tariff')}
        </Typography>
        <Chip
          label={getPlanDisplayName(selected_plan)}
          size="small"
          sx={{
            bgcolor: 'rgba(255, 152, 0, 0.1)',
            color: 'warning.main',
            fontSize: '0.7rem',
            fontWeight: 600,
            height: '20px'
          }}
        />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {Math.floor(included_units || 0).toLocaleString()} {t('calculator.voicebotMinIncluded', 'min included')}
          {extra_units > 0 && `, +${Math.floor(extra_units).toLocaleString()} ${t('calculator.voicebotExtraMin', 'extra min')} × $${parseFloat(price_per_unit || 0).toFixed(3)}`}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RecordVoiceOver sx={{ color: '#333', fontSize: 20, mr: 1 }} />
            <Typography sx={{ fontWeight: 500 }}>
              {t('calculator.voicebotLabel', 'VoiceBot')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('calculator.voicebotSettings', 'VoiceBot Settings')}>
              <IconButton
                size="small"
                onClick={handleSettingsClick}
                sx={{
                  color: hasAnyCustomSettings ? '#00C853' : '#757575',
                  mr: 1,
                  '&:hover': { color: '#00C853' }
                }}
              >
                <Settings fontSize="small" />
              </IconButton>
            </Tooltip>

            <IconButton
              size="small"
              onClick={() => toggleProduct('voicebot')}
              sx={{
                color: '#333',
                '&:hover': { color: '#00C853' }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Minutes Slider */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, mb: 0.5 }}>
          {t('calculator.voicebotMinutesInput', 'Number of minutes')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Slider
              value={Math.min(localSliderValue, 20000)} // Cap display at 20000 for slider
              onChange={(_, v) => {
                setLocalSliderValue(Number(v));
                setInputValue(v.toString());
              }}
              onChangeCommitted={(_, v) => {
                const value = Number(v);
                setVoicebotMinutes(value);
              }}
              min={1100}
              max={20000}
              step={1}
              valueLabelDisplay="off"
              sx={{
                color: '#00C853',
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                  backgroundColor: '#00C853',
                }
              }}
            />
          </Box>
          <TextField
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            type="number"
            variant="outlined"
            size="small"
            sx={{ width: 90 }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Box>

        {/* Pricing Information */}
        {renderPricingInfo()}

      </Box>

      {/* Settings Dialog */}
      <VoiceBotSettingsDialog />
    </>
  );
};

export default VoiceBot;
